import { ClassValue } from 'clsx';
import { isNil } from 'lodash';
import { Icon } from '@shared/components/Icon';
import { Image, UserImage } from '@shared/components/Image';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { Signature, SignatureField } from '@shared/data/entity/fields/SignatureField';
import { formatDate } from '@shared/util/date';
import { useImageSrcAndDimensions } from '@shared/util/hooks';
import { CollectionViewFieldContainer } from './CollectionViewFieldContainer';
import { FieldVariant } from './FieldVariant';
import { useFieldUser } from './PersonRefsContext';
import { getShortName } from './util/getShortName';
import classNames from './SignatureCollectionViewField.module.css';

interface Props {
  variant: FieldVariant;
  className?: ClassValue;
  field: SignatureField;
}

/**
 * Default: the signature itself. Compact: a pen, the signer's avatar and the date.
 * Inline: a pen and the signer's short name, or the name typed with the signature.
 */
export function SignatureCollectionViewField({ field, variant, className }: Props) {
  if (field.isEmpty) {
    return null;
  }

  const isInline = variant === 'inline';
  const isCompact = variant === 'compact';
  const isDefault = variant === 'default';

  return (
    <CollectionViewFieldContainer
      fieldType={field.fieldType}
      variant={variant}
      className={[classNames.signatureCollectionViewField, className]}
    >
      {isInline && <InlineSignature field={field} />}
      {isCompact && <CompactSignature field={field} />}
      {isDefault && <DefaultSignature image={field.signature} />}
    </CollectionViewFieldContainer>
  );
}

function DefaultSignature({ image }: { image: Signature }) {
  const { src } = useImageSrcAndDimensions({ image });
  return <Image source={{ uri: src }} className={classNames.image} />;
}

/** Without a signing user or date, those parts are left out. */
function CompactSignature({ field }: { field: SignatureField }) {
  const { userId, date } = field.signature;
  const { user } = useFieldUser(userId);
  return (
    <>
      <View row centerVertically className={classNames.marks}>
        <Icon name="signature-ballpoint-pen" gridSize={3.5} className={classNames.icon} />
        {!isNil(userId) && <UserImage user={user} size="small" className={classNames.avatar} />}
      </View>
      {!isNil(date) && (
        <Text weight="normal" numberOfLines={1}>
          {formatDate(date)}
        </Text>
      )}
    </>
  );
}

function InlineSignature({ field }: { field: SignatureField }) {
  const { user } = useFieldUser(field.signature.userId);
  return (
    <>
      <Icon name="signature-ballpoint-pen" gridSize={2} className={classNames.inlineIcon} />
      {getShortName(user ?? { fullName: field.signature.name })}
    </>
  );
}
